/**
 * Homepage Component
 * 
 * Main homepage for Brave Things Books website featuring:
 * - Hero section with organic background shapes inspired by Google's design
 * - Book showcase highlighting "Where the Brave Things Grow"
 * - Features section with improved spacing and hover animations
 * - CTA section with forest/golden theme
 * - Footer with navigation and social links
 * 
 * Updated with Google-inspired design elements: organic shapes, generous spacing, subtle animations
 */

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { HeroSection } from "@/components/website/hero-section";
import { WhatMakesSpecial } from "@/components/website/what-makes-special";
import { BookPreviewSection } from "@/components/website/book-preview-section";
import { FeatureCard } from "@/components/website/feature-card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Heart, 
  Brain, 
  Users, 
  Sparkles, 
  Play
} from "lucide-react";

// Features data
const features = [
  {
    icon: Heart,
    title: "Social-Emotional Learning",
    description: "Develop emotional intelligence, empathy, and self-awareness through engaging interactive stories.",
    color: "bg-coral-50 text-coral-600 border-coral-200"
  },
  {
    icon: Brain,
    title: "Evidence-Based Content",
    description: "Created by child development experts using proven methodologies for effective learning outcomes.",
    color: "bg-earth-50 text-earth-600 border-earth-200"
  },
  {
    icon: Users,
    title: "Family Engagement",
    description: "Includes discussion guides and activities for parents to reinforce learning at home.",
    color: "bg-forest-50 text-forest-600 border-forest-200"
  },
  {
    icon: Sparkles,
    title: "Interactive Experiences",
    description: "Children actively participate in the story through choices, activities, and reflection moments.",
    color: "bg-golden-50 text-golden-600 border-golden-200"
  }
];

export default function Homepage() {
  // Function to scroll to top when buttons are clicked
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white overflow-hidden">

      {/* Hero Section with Organic Background Shapes */}
      <section className="relative">
        {/* Large Organic Background Shapes - Google-inspired */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Main coral blob shape - top right */}
          <motion.div
            className="absolute -top-32 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-coral-300/30 to-coral-400/20 rounded-full"
            style={{
              borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%",
            }}
            animate={{
              borderRadius: [
                "60% 40% 30% 70% / 60% 30% 70% 40%",
                "30% 60% 70% 40% / 50% 60% 30% 60%",
                "60% 40% 30% 70% / 60% 30% 70% 40%"
              ]
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          {/* Secondary shape - bottom left */}
          <motion.div
            className="absolute -bottom-40 -left-32 w-[500px] h-[500px] bg-gradient-to-tr from-golden-300/20 to-golden-400/15 rounded-full"
            style={{
              borderRadius: "40% 60% 60% 40% / 70% 30% 60% 40%",
            }}
            animate={{
              borderRadius: [
                "40% 60% 60% 40% / 70% 30% 60% 40%",
                "60% 40% 30% 70% / 40% 60% 30% 70%",
                "40% 60% 60% 40% / 70% 30% 60% 40%"
              ]
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
          />

          {/* Tertiary accent shape - center */}
          <motion.div
            className="absolute top-1/2 left-1/3 w-[300px] h-[300px] bg-gradient-to-br from-forest-300/15 to-forest-400/10 rounded-full"
            style={{
              borderRadius: "50% 50% 50% 50% / 60% 40% 60% 40%",
            }}
            animate={{
              borderRadius: [
                "50% 50% 50% 50% / 60% 40% 60% 40%",
                "40% 60% 40% 60% / 50% 50% 50% 50%",
                "50% 50% 50% 50% / 60% 40% 60% 40%"
              ]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 4
            }}
          />
        </div>

        <HeroSection />
      </section>

      {/* What Makes It Special Section */}
      <WhatMakesSpecial />

      {/* Book Preview Section */}
      <BookPreviewSection />

      {/* Features Section - Google-inspired Cards and Spacing */}
      <section id="about" className="py-32 lg:py-48 bg-white relative">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute top-20 left-10 w-[350px] h-[350px] bg-gradient-to-br from-golden-200/20 to-golden-300/15 rounded-full"
            style={{ borderRadius: "50% 50% 50% 50% / 70% 30% 70% 30%" }}
            animate={{
              borderRadius: [
                "50% 50% 50% 50% / 70% 30% 70% 30%",
                "30% 70% 30% 70% / 50% 50% 50% 50%",
                "50% 50% 50% 50% / 70% 30% 70% 30%"
              ]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header - Enhanced Spacing */}
          <div className="text-center mb-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              <Badge className="mb-8 bg-forest-100 text-forest-700 border-forest-200 px-6 py-3 text-base">
                🎯 Why Choose Brave Things Books
              </Badge>
              <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight">
                Learning That Grows With Your Child
              </h2>
              <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Our interactive books create meaningful learning experiences that children love and parents trust.
              </p>
            </motion.div>
          </div>

          {/* Enhanced Features Grid with Better Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ 
                  duration: 0.8, 
                  delay: index * 0.1,
                  ease: "easeOut"
                }}
                viewport={{ once: true }}
                whileHover={{ 
                  y: -8,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
              >
                <FeatureCard
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  color={feature.color}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Enhanced with Organic Shape */}
      <section className="relative py-32 lg:py-48 bg-gradient-to-r from-forest-600 to-golden-600 overflow-hidden">
        {/* Organic background shape */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-white/10 rounded-full"
            style={{ borderRadius: "60% 40% 70% 30% / 40% 60% 30% 70%" }}
            animate={{
              borderRadius: [
                "60% 40% 70% 30% / 40% 60% 30% 70%",
                "30% 70% 40% 60% / 60% 40% 70% 30%",
                "60% 40% 70% 30% / 40% 60% 30% 70%"
              ]
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true }}
          >
            <h2 className="text-5xl lg:text-6xl font-bold text-white mb-8 leading-tight">
              Ready to Start Your Child's Journey?
            </h2>
            <p className="text-xl lg:text-2xl text-cream-100 mb-12 max-w-3xl mx-auto leading-relaxed">
              Join other families discovering the joy of interactive learning. 
              Start reading "Where the Brave Things Grow" today.
            </p>
            <div className="flex justify-center">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <Link to="/members">
                  <Button 
                    size="lg" 
                    className="bg-white text-forest-600 bg-cream-50 px-12 py-6 text-xl font-semibold rounded-full shadow-2xl shadow-3xl all duration-300"
                    onClick={scrollToTop}
                  >
                    <Play className="w-7 h-7 mr-4" />
                    Start the Journey
                  </Button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
